import * as Phaser from 'phaser';
import { GameEngine } from '../engine/GameEngine';
import { SymbolType, SpinResult } from '../types/game.types';
import { SYMBOLS } from '../config/symbols.config';
import { getCurrentTheme, ThemeConfig } from '../config/themes.config';
import { ThemeSelector } from '../components/ThemeSelector';
import { UserManager } from '../components/UserManager';
import { ApiAuth, AuthenticatedUser } from '../components/ApiAuth';
import { SoundControlsPanel } from '../components/SoundControlsPanel';

export class GameScene extends Phaser.Scene {
    private gameEngine: GameEngine;
    private reelContainers: Phaser.GameObjects.Container[] = [];
    private symbolSprites: Phaser.GameObjects.Container[][] = [];
    private isSpinning: boolean = false;
    private balanceText!: Phaser.GameObjects.Text;
    private betText!: Phaser.GameObjects.Text;
    private winText!: Phaser.GameObjects.Text;
    private spinButton!: Phaser.GameObjects.Container;
    private freeSpinsText!: Phaser.GameObjects.Text;
    
    // Betting controls
    private linesText!: Phaser.GameObjects.Text;
    private betPerLineText!: Phaser.GameObjects.Text;
    private linesUpButton!: Phaser.GameObjects.Container;
    private linesDownButton!: Phaser.GameObjects.Container;
    private betUpButton!: Phaser.GameObjects.Container;
    private betDownButton!: Phaser.GameObjects.Container;
    
    // Payline indicators
    private paylineGraphics: Phaser.GameObjects.Graphics[] = [];
    private paylineCycleTimer?: Phaser.Time.TimerEvent;
    
    // Theme configuration
    private currentTheme: ThemeConfig;
    private themeSelector?: ThemeSelector;
    private soundControls?: SoundControlsPanel;
    
    // Player Rankings and Stats System
    private playerName: string = '';
    private playerRankings: any[] = [];
    private rankingTexts: Phaser.GameObjects.Text[] = [];
    private playerStatsTexts: Phaser.GameObjects.Text[] = [];
    private nameInputText!: Phaser.GameObjects.Text;

    // Authentication and stats tracking
    private auth: ApiAuth | null = null;
    private currentUser: AuthenticatedUser | null = null;
    private playerStats = {
        handsPlayed: 0,
        bestHand: '',
        biggestWin: 0,
        totalWon: 0,
        totalLost: 0
    };
    
    private readonly REEL_WIDTH = 140;
    private readonly REEL_HEIGHT = 420;
    private readonly SYMBOL_SIZE = 512;
    private readonly REEL_SPACING = 10;
    
    constructor() {
        super({ key: 'GameScene' });
        this.gameEngine = new GameEngine();
        this.currentTheme = getCurrentTheme();
        
        // Make game engine available globally for admin panel
        (window as any).gameEngine = this.gameEngine;
        
        // Initialize authentication
        this.auth = ApiAuth.getInstance();
        this.currentUser = this.auth.getUser();

        // Get player name from authenticated user or default
        this.playerName = this.currentUser?.name || 'Guest';

        // Initialize player rankings
        this.loadPlayerRankings();

        // Set initial balance from authenticated user
        const userManager = UserManager.getInstance();
        const currentUser = userManager.getCurrentUser();
        if (currentUser?.balance !== undefined) {
            this.gameEngine.setBalance(currentUser.balance);
        }
    }
    
    preload(): void {
        console.log('=== PRELOAD STARTED ===');
        this.loadThemeAssets();
        console.log('=== THEME ASSETS QUEUED ===');
    }
    
    create(): void {
        const { width, height} = this.cameras.main;

        console.log('=== CREATE STARTED ===');

        // Debug: Check what textures were loaded
        console.log(`Test texture 'test_treasure' exists: ${this.textures.exists('test_treasure')}`);

        Object.values(SymbolType).forEach(symbolType => {
            const exists = this.textures.exists(symbolType);
            console.log(`Texture '${symbolType}' exists: ${exists}`);
        });

        this.createBackground();
        this.createTitleBoard();
        this.createMenuButton();
        this.createSideDecorations();
        this.createReels();
        this.createUI();
        this.setupInputHandlers();
        this.setupNameInput();
        this.createThemeSelector();
        this.createSoundControls();

        console.log('=== CREATE COMPLETED ===');
    }
    
    private loadThemeAssets(): void {
        console.log('Current theme symbols path:', this.currentTheme.symbols.path);
        
        // Set up global load event listeners
        this.load.on('filecomplete', (key: string, type: string, data: any) => {
            console.log(`✅ File loaded: ${key} (${type})`);
        });
        
        this.load.on('loaderror', (file: any) => {
            console.log(`❌ Load error: ${file.key} from ${file.src}`);
        });
        
        // Load symbol images for current theme
        Object.values(SymbolType).forEach(symbolType => {
            const symbolConfig = SYMBOLS[symbolType];
            if (symbolConfig) {
                const imagePath = `${this.currentTheme.symbols.path}${symbolConfig.image}`;
                console.log(`Queuing: ${symbolType} from ${imagePath}`);
                
                // Try to load the image
                this.load.image(symbolType, imagePath);
            }
        });
        
        // Load UI assets
        if (this.currentTheme.ui.titleImage) {
            this.load.image('titleBoard', this.currentTheme.ui.titleImage);
        }
        
        if (this.currentTheme.ui.leftDecoration) {
            this.load.image('leftDecoration', this.currentTheme.ui.leftDecoration);
        }
        
        if (this.currentTheme.ui.rightDecoration) {
            this.load.image('rightDecoration', this.currentTheme.ui.rightDecoration);
        }
        
        // Handle load errors gracefully
        this.load.on('loaderror', (file: any) => {
            console.log(`Asset not found (will use placeholder): ${file.src}`);
        });
    }
    
    private createSymbolGraphics(container: Phaser.GameObjects.Container, symbolType: SymbolType): void {
        // Check if we have an actual image loaded for this symbol
        const textureExists = this.textures.exists(symbolType);
        console.log(`Creating symbol ${symbolType}, texture exists: ${textureExists}`);
        
        if (textureExists) {
            this.createActualSymbol(container, symbolType);
        } else {
            this.createPlaceholderSymbol(container, symbolType);
        }
    }
    
    private createActualSymbol(container: Phaser.GameObjects.Container, symbolType: SymbolType): void {
        const displaySize = 130; // Visual size in game
        
        // Create image sprite
        const symbolImage = this.add.image(0, 0, symbolType);
        
        // Scale to fit display size
        const scale = displaySize / Math.max(symbolImage.width, symbolImage.height);
        symbolImage.setScale(scale);
        
        // Add subtle glow effect
        const glow = this.add.graphics();
        glow.fillStyle(0xFFFFFF, 0.2);
        glow.fillRoundedRect(-displaySize/2 - 3, -displaySize/2 - 3, 
                           displaySize + 6, displaySize + 6, 15);
        
        // Add border
        const border = this.add.graphics();
        border.lineStyle(2, 0xFFFFFF, 0.8);
        border.strokeRoundedRect(-displaySize/2, -displaySize/2, 
                               displaySize, displaySize, 12);
        
        container.add([glow, symbolImage, border]);
    }
    
    private createPlaceholderSymbol(container: Phaser.GameObjects.Container, symbolType: SymbolType): void {
        const colors: Record<string, number> = {
            'treasure_chest': 0xFFD700,
            'neon_diamond': 0x00FFFF,
            'showgirl_hat': 0xFF69B4,
            'sports_car': 0xFF0000,
            'dice': 0x9370DB,
            'martini': 0x00FF00,
            'coins': 0xFFA500,
            'horseshoe': 0x8B4513,
            'ace': 0xFF1493,
            'king': 0x4169E1,
            'queen': 0xFF00FF,
            'jack': 0x00CED1,
            'ten': 0x32CD32,
            'wild': 0xFFFF00,
            'scatter': 0xFF00FF,
            'mini_game': 0x00FF00
        };
        
        const labels: Record<string, string> = {
            'treasure_chest': 'GOLD',
            'neon_diamond': 'DIAM',
            'showgirl_hat': 'HAT',
            'sports_car': 'CAR',
            'dice': 'DICE',
            'martini': 'DRINK',
            'coins': 'COIN',
            'horseshoe': 'SHOE',
            'ace': 'A',
            'king': 'K',
            'queen': 'Q',
            'jack': 'J',
            'ten': '10',
            'wild': 'WILD',
            'scatter': 'SCAT',
            'mini_game': 'MINI'
        };
        
        const color = colors[symbolType] || 0xFFFFFF;
        const label = labels[symbolType] || '?';
        
        // Use display size (smaller than actual symbol resolution)
        const displaySize = 130; // Keep same visual size but higher resolution source
        
        // Background glow
        const glow = this.add.graphics();
        glow.fillStyle(color, 0.3);
        glow.fillRoundedRect(-displaySize/2 - 3, -displaySize/2 - 3, 
                           displaySize + 6, displaySize + 6, 15);
        
        // Main background
        const bg = this.add.graphics();
        bg.fillStyle(color, 1);
        bg.fillRoundedRect(-displaySize/2, -displaySize/2, 
                          displaySize, displaySize, 12);
        
        // Border
        bg.lineStyle(3, 0xFFFFFF, 1);
        bg.strokeRoundedRect(-displaySize/2, -displaySize/2, 
                           displaySize, displaySize, 12);
        
        // Text with symbol name and filename
        const symbolInfo = SYMBOLS[symbolType];
        const filename = symbolInfo ? symbolInfo.image : `${symbolType}.png`;
        
        const text = this.add.text(0, 0, label, {
            fontFamily: 'Arial',
            fontSize: '20px',
            color: '#FFFFFF',
            stroke: '#000000',
            strokeThickness: 2,
            fontStyle: 'bold'
        });
        text.setOrigin(0.5);
        
        // Add filename text below the main label
        const filenameText = this.add.text(0, 25, filename, {
            fontFamily: 'Arial',
            fontSize: '10px',
            color: '#FFFFFF',
            stroke: '#000000',
            strokeThickness: 1
        });
        filenameText.setOrigin(0.5);
        
        // Add resolution text
        const sizeText = this.add.text(0, -25, '512x512px', {
            fontFamily: 'Arial',
            fontSize: '10px',
            color: '#FFFF00',
            stroke: '#000000',
            strokeThickness: 1
        });
        sizeText.setOrigin(0.5);
        
        container.add([glow, bg, text, filenameText, sizeText]);
    }
    
    private createTitleBoard(): void {
        const { width } = this.cameras.main;
        
        const titleWidth = 600;
        const titleHeight = 100;
        const titleX = (width - titleWidth) / 2;
        const titleY = 10;
        
        // Try to load the actual title board image
        if (this.textures.exists('titleBoard')) {
            const titleImage = this.add.image(width / 2, titleY + titleHeight / 2, 'titleBoard');
            titleImage.setDisplaySize(titleWidth, titleHeight);
        } else {
            // Fallback to placeholder
            const titleBg = this.add.graphics();
            titleBg.fillStyle(0x000000, 0.8);
            titleBg.fillRoundedRect(titleX, titleY, titleWidth, titleHeight, 10);
            titleBg.lineStyle(3, 0x00FFFF, 1);
            titleBg.strokeRoundedRect(titleX, titleY, titleWidth, titleHeight, 10);
            
            const titleText = this.add.text(width / 2, titleY + titleHeight / 2, 
                'REEL HEROES', {
                fontFamily: 'Orbitron',
                fontSize: '36px',
                color: '#00FFFF',
                stroke: '#FF00FF',
                strokeThickness: 3,
                align: 'center'
            });
            titleText.setOrigin(0.5);
        }
    }
    
    private createMenuButton(): void {
        const menuButton = this.createControlButton(50, 30, 'MENU', {
            backgroundColor: 0xFF6600,
            borderColor: 0xFFFFFF,
            textColor: '#000000'
        });
        menuButton.on('pointerdown', () => {
            this.scene.start('MenuScene');
        });
    }
    
    private createSideDecorations(): void {
        const { width, height } = this.cameras.main;
        
        // Left side - Player controls
        const leftWidth = 250;
        const sideHeight = 400;
        const leftX = 10;
        const leftY = (height - sideHeight) / 2;
        
        // Create player name input on left side
        this.createLeftSideControls(leftX, leftY, leftWidth);
        
        // Right side - High Score Board (wider for text)
        const rightWidth = 180;  // Increased from 150 to fit "HIGH SCORES" text
        const rightX = width - rightWidth - 10;
        const rightY = leftY;
        
        this.createPlayerRankings(rightX, rightY, rightWidth, sideHeight);
    }
    
    private createLeftSideControls(x: number, y: number, width: number): void {
        // Background for left side controls
        const bg = this.add.graphics();
        bg.fillStyle(0x000000, 0.8);
        bg.fillRoundedRect(x, y, width, 200, 10);
        bg.lineStyle(2, 0x00FFFF, 1);
        bg.strokeRoundedRect(x, y, width, 200, 10);
        
        // Title
        const title = this.add.text(x + width / 2, y + 25,
            'PLAYER INFO', {
            fontFamily: 'Orbitron',
            fontSize: '18px',
            color: '#00FFFF',
            stroke: '#000000',
            strokeThickness: 2
        });
        title.setOrigin(0.5);
        
        // Player name (click to edit)
        this.nameInputText = this.add.text(x + width / 2, y + 60,
            `${this.playerName}`, {
            fontFamily: 'Orbitron',
            fontSize: '20px',
            color: '#FFFFFF',
            stroke: '#FF00FF',
            strokeThickness: 2
        });
        this.nameInputText.setOrigin(0.5);
        this.nameInputText.setInteractive();
        this.nameInputText.on('pointerdown', () => this.promptForName());
        
        // Add hint text
        const hintText = this.add.text(x + width / 2, y + 85,
            '(Click to change)', {
            fontFamily: 'Orbitron',
            fontSize: '10px',
            color: '#888888'
        });
        hintText.setOrigin(0.5);
        
        // Player Stats Display on left side
        const statsY = y + 140;

        const statsTitle = this.add.text(x + width / 2, statsY, 'PLAYER STATS', {
            fontFamily: 'Orbitron',
            fontSize: '16px',
            color: '#FFD700',
            stroke: '#000000',
            strokeThickness: 2
        });
        statsTitle.setOrigin(0.5);

        // Create stat text entries
        const statLabels = ['Spins Played:', 'Best Win:', 'Total Won:'];

        for (let i = 0; i < statLabels.length; i++) {
            const statsText = this.add.text(x + 10, statsY + 35 + (i * 25), statLabels[i], {
                fontFamily: 'Orbitron',
                fontSize: '12px',
                color: '#00FFFF'
            });
            this.playerStatsTexts.push(statsText);
        }

        this.updatePlayerStatsDisplay();
    }
    
    private createBackground(): void {
        const { width, height } = this.cameras.main;
        
        // Theme-based background colors
        const bg = this.add.graphics();
        const [color1, color2] = this.currentTheme.colors.background;
        bg.fillGradientStyle(color1, color1, color2, color2, 1);
        bg.fillRect(0, 0, width, height);
        
        // Add themed geometric pattern
        const pattern = this.add.graphics();
        pattern.lineStyle(1, this.currentTheme.colors.uiAccent2, 0.2);
        
        // Grid pattern reminiscent of 80s computer graphics
        for (let x = 0; x < width; x += 40) {
            pattern.moveTo(x, 0);
            pattern.lineTo(x, height);
        }
        for (let y = 0; y < height; y += 40) {
            pattern.moveTo(0, y);
            pattern.lineTo(width, y);
        }
        pattern.strokePath();
        
        // Add themed sparkle stars
        for (let i = 0; i < 50; i++) {
            const themeColors = [
                0xFFFFFF,
                this.currentTheme.colors.uiAccent1,
                this.currentTheme.colors.uiAccent2,
                this.currentTheme.colors.uiAccent3
            ];
            const star = this.add.circle(
                Phaser.Math.Between(0, width),
                Phaser.Math.Between(0, height),
                Phaser.Math.Between(1, 3),
                themeColors[Math.floor(Math.random() * themeColors.length)],
                Phaser.Math.FloatBetween(0.3, 0.8)
            );
            
            this.tweens.add({
                targets: star,
                alpha: { from: star.alpha, to: 0 },
                duration: Phaser.Math.Between(1000, 3000),
                repeat: -1,
                yoyo: true
            });
        }
    }
    
    private createReels(): void {
        const { width, height } = this.cameras.main;
        const startX = (width - (5 * this.REEL_WIDTH + 4 * this.REEL_SPACING)) / 2;
        const startY = (height - this.REEL_HEIGHT) / 2 - 50;
        
        // Create retro-futuristic reel frame background
        const frameGraphics = this.add.graphics();
        const frameMargin = 15;
        const frameX = startX - frameMargin;
        const frameY = startY - frameMargin;
        const frameWidth = 5 * this.REEL_WIDTH + 4 * this.REEL_SPACING + frameMargin * 2;
        const frameHeight = this.REEL_HEIGHT + frameMargin * 2;
        
        // Main frame with theme colors
        frameGraphics.fillStyle(this.currentTheme.colors.reelFrame, 0.8);
        frameGraphics.fillRoundedRect(frameX, frameY, frameWidth, frameHeight, 10);
        
        // Themed border layers
        frameGraphics.lineStyle(3, this.currentTheme.colors.uiAccent1, 1);
        frameGraphics.strokeRoundedRect(frameX, frameY, frameWidth, frameHeight, 10);
        frameGraphics.lineStyle(1, this.currentTheme.colors.uiAccent2, 0.8);
        frameGraphics.strokeRoundedRect(frameX + 2, frameY + 2, frameWidth - 4, frameHeight - 4, 8);
        
        for (let r = 0; r < 5; r++) {
            const x = startX + r * (this.REEL_WIDTH + this.REEL_SPACING);
            
            // Individual reel frame with themed styling
            const reelBg = this.add.graphics();
            reelBg.fillStyle(this.currentTheme.colors.reelFrame, 0.7);
            reelBg.fillRoundedRect(x - 5, startY - 5, this.REEL_WIDTH + 10, this.REEL_HEIGHT + 10, 10);
            
            // Themed border
            reelBg.lineStyle(3, this.currentTheme.colors.reelBorder, 1);
            reelBg.strokeRoundedRect(x - 5, startY - 5, this.REEL_WIDTH + 10, this.REEL_HEIGHT + 10, 10);
            
            // Create mask for this reel to clip rolling symbols
            const reelMask = this.add.graphics();
            reelMask.fillStyle(0xffffff);
            reelMask.fillRoundedRect(x, startY, this.REEL_WIDTH, this.REEL_HEIGHT, 5);
            const mask = reelMask.createGeometryMask();
            
            const container = this.add.container(x + this.REEL_WIDTH / 2, startY + this.REEL_HEIGHT / 2);
            container.setMask(mask);
            this.reelContainers.push(container);
            
            const reelSymbols: Phaser.GameObjects.Container[] = [];
            
            const initialSymbolsPerReel = [
                [SymbolType.TREASURE_CHEST, SymbolType.KING, SymbolType.ACE],
                [SymbolType.NEON_DIAMOND, SymbolType.QUEEN, SymbolType.KING],
                [SymbolType.WILD, SymbolType.DICE, SymbolType.JACK],
                [SymbolType.SPORTS_CAR, SymbolType.MARTINI, SymbolType.TEN],
                [SymbolType.SCATTER, SymbolType.COINS, SymbolType.QUEEN]
            ];
            
            // Calculate proper spacing for 3 symbols in the reel height
            const symbolSpacing = (this.REEL_HEIGHT - 20) / 3; // 20px padding
            const symbolStartY = -this.REEL_HEIGHT/2 + symbolSpacing/2 + 10; // 10px top padding
            
            for (let row = 0; row < 3; row++) {
                const y = symbolStartY + (row * symbolSpacing);
                const symbolType = initialSymbolsPerReel[r][row];
                
                // Create a symbol using graphics directly
                const symbolContainer = this.add.container(0, y);
                symbolContainer.setData('symbolType', symbolType);
                
                this.createSymbolGraphics(symbolContainer, symbolType);
                
                container.add(symbolContainer);
                reelSymbols.push(symbolContainer);
            }
            
            this.symbolSprites.push(reelSymbols);
        }
    }
    
    private createUI(): void {
        const { width, height } = this.cameras.main;
        
        const uiStyle = {
            fontFamily: 'Orbitron',
            fontSize: '24px',
            color: '#00FFFF',
            stroke: '#FF00FF',
            strokeThickness: 2,
            shadow: {
                offsetX: 2,
                offsetY: 2,
                color: '#000000',
                blur: 4,
                fill: true
            }
        };
        
        // Balance will be created in the top panel
        this.createTopInfoPanel();
        
        this.freeSpinsText = this.add.text(width - 50, 130, '', {
            ...uiStyle,
            fontSize: '28px',
            color: '#FF00FF'
        });
        this.freeSpinsText.setOrigin(1, 0);
        
        // Betting controls
        this.createBettingControls();
        
        // Show active paylines initially
        this.updateActivePaylineDisplay();
    }
    
    private createTopInfoPanel(): void {
        // Top panel can be used for other info if needed
        // Balance has been moved to bottom control panel
    }
    
    private createBettingControls(): void {
        this.createBottomControlPanel();
        this.setupBettingHandlers();
        this.createPaylineIndicators();
    }
    
    private createBottomControlPanel(): void {
        const { width, height } = this.cameras.main;
        
        // Create bottom control panel background
        const panelHeight = 120;
        const panelY = height - panelHeight;
        
        const panel = this.add.graphics();
        panel.fillStyle(0x000000, 0.8);
        panel.fillRoundedRect(0, panelY, width, panelHeight, 10);
        
        // Add neon border
        panel.lineStyle(3, 0x00FFFF, 1);
        panel.strokeRoundedRect(0, panelY, width, panelHeight, 10);
        panel.lineStyle(1, 0xFF00FF, 0.8);
        panel.strokeRoundedRect(2, panelY + 2, width - 4, panelHeight - 4, 8);
        
        // Control panel sections
        this.createBetControls(panelY);
        this.createSpinButtonNew(panelY);
    }
    
    private createBetControls(panelY: number): void {
        const { width } = this.cameras.main;
        
        const controlStyle = {
            fontFamily: 'Orbitron',
            fontSize: '16px',
            color: '#00FFFF',
            stroke: '#FF00FF',
            strokeThickness: 1
        };
        
        const valueStyle = {
            fontFamily: 'Orbitron',
            fontSize: '24px',
            color: '#FFFFFF',
            stroke: '#FF00FF',
            strokeThickness: 2
        };
        
        // Balance section (far left)
        const balanceBg = this.add.graphics();
        balanceBg.fillStyle(0x000000, 0.9);
        balanceBg.fillRoundedRect(20, panelY + 15, 140, 90, 8);
        balanceBg.lineStyle(2, 0x00FF00, 1);
        balanceBg.strokeRoundedRect(20, panelY + 15, 140, 90, 8);
        
        this.add.text(90, panelY + 25, 'BALANCE', controlStyle).setOrigin(0.5);
        this.balanceText = this.add.text(90, panelY + 50, '$1000', {
            ...valueStyle,
            color: '#00FF00'
        }).setOrigin(0.5);
        
        // Total Bet section (second from left)
        const totalBetBg = this.add.graphics();
        totalBetBg.fillStyle(0x000000, 0.9);
        totalBetBg.fillRoundedRect(180, panelY + 15, 120, 90, 8);
        totalBetBg.lineStyle(2, 0xFF00FF, 1);
        totalBetBg.strokeRoundedRect(180, panelY + 15, 120, 90, 8);
        
        this.add.text(240, panelY + 25, 'TOTAL BET', controlStyle).setOrigin(0.5);
        this.betText = this.add.text(240, panelY + 50, '$20', valueStyle).setOrigin(0.5);
        
        // Lines control (center)
        const linesBg = this.add.graphics();
        linesBg.fillStyle(0x000000, 0.9);
        linesBg.fillRoundedRect(320, panelY + 15, 120, 90, 8);
        linesBg.lineStyle(2, 0x00FFFF, 1);
        linesBg.strokeRoundedRect(320, panelY + 15, 120, 90, 8);
        
        this.add.text(380, panelY + 25, 'LINES', controlStyle).setOrigin(0.5);
        this.linesText = this.add.text(380, panelY + 50, '20', valueStyle).setOrigin(0.5);
        
        this.linesDownButton = this.createPanelButton(345, panelY + 75, '-');
        this.linesUpButton = this.createPanelButton(415, panelY + 75, '+');
        
        // Bet per line control (center right)
        const betLineBg = this.add.graphics();
        betLineBg.fillStyle(0x000000, 0.9);
        betLineBg.fillRoundedRect(460, panelY + 15, 120, 90, 8);
        betLineBg.lineStyle(2, 0xFFFF00, 1);
        betLineBg.strokeRoundedRect(460, panelY + 15, 120, 90, 8);
        
        this.add.text(520, panelY + 25, 'BET/LINE', controlStyle).setOrigin(0.5);
        this.betPerLineText = this.add.text(520, panelY + 50, '$1', valueStyle).setOrigin(0.5);
        
        this.betDownButton = this.createPanelButton(485, panelY + 75, '-');
        this.betUpButton = this.createPanelButton(555, panelY + 75, '+');
        
        // Win display (right side) - made wider to accommodate "BIG WIN $XXX"
        const winWidth = 160; // Increased from 120 to 160
        const winBg = this.add.graphics();
        winBg.fillStyle(0x000000, 0.9);
        winBg.fillRoundedRect(width - winWidth - 20, panelY + 15, winWidth, 90, 8);
        winBg.lineStyle(2, 0x00FF00, 1);
        winBg.strokeRoundedRect(width - winWidth - 20, panelY + 15, winWidth, 90, 8);
        
        this.add.text(width - winWidth/2 - 20, panelY + 25, 'WIN', controlStyle).setOrigin(0.5);
        this.winText = this.add.text(width - winWidth/2 - 20, panelY + 50, '$0', {
            ...valueStyle,
            color: '#00FF00'
        }).setOrigin(0.5);
        this.winText.setVisible(true); // Always visible in panel
    }
    
    private createPanelButton(x: number, y: number, text: string): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        
        const bg = this.add.graphics();
        bg.fillStyle(0xFF00FF, 1);
        bg.fillRoundedRect(-12, -12, 24, 24, 4);
        bg.lineStyle(1, 0x00FFFF, 1);
        bg.strokeRoundedRect(-12, -12, 24, 24, 4);
        
        const buttonText = this.add.text(0, 0, text, {
            fontFamily: 'Orbitron',
            fontSize: '16px',
            color: '#FFFFFF',
            fontStyle: 'bold'
        });
        buttonText.setOrigin(0.5);
        
        container.add([bg, buttonText]);
        container.setInteractive(new Phaser.Geom.Circle(0, 0, 15), Phaser.Geom.Circle.Contains);
        
        container.on('pointerover', () => {
            container.setScale(1.1);
            bg.clear();
            bg.fillStyle(0x00FFFF, 1);
            bg.fillRoundedRect(-12, -12, 24, 24, 4);
            bg.lineStyle(1, 0xFF00FF, 1);
            bg.strokeRoundedRect(-12, -12, 24, 24, 4);
        });
        
        container.on('pointerout', () => {
            container.setScale(1);
            bg.clear();
            bg.fillStyle(0xFF00FF, 1);
            bg.fillRoundedRect(-12, -12, 24, 24, 4);
            bg.lineStyle(1, 0x00FFFF, 1);
            bg.strokeRoundedRect(-12, -12, 24, 24, 4);
        });
        
        return container;
    }
    
    private createSpinButtonNew(panelY: number): void {
        const { width } = this.cameras.main;
        const buttonX = width - 280;
        const buttonY = panelY + 60;
        
        this.spinButton = this.add.container(buttonX, buttonY);
        
        // Main button background
        const buttonBg = this.add.graphics();
        buttonBg.fillStyle(0xFF00FF, 1);
        buttonBg.fillRoundedRect(-50, -25, 100, 50, 25);
        
        // Button border
        buttonBg.lineStyle(4, 0x00FFFF, 1);
        buttonBg.strokeRoundedRect(-50, -25, 100, 50, 25);
        
        // Inner highlight
        buttonBg.lineStyle(2, 0xFFFFFF, 0.6);
        buttonBg.strokeRoundedRect(-47, -22, 94, 44, 22);
        
        const buttonText = this.add.text(0, 0, 'SPIN', {
            fontFamily: 'Orbitron',
            fontSize: '24px',
            color: '#FFFFFF',
            stroke: '#000000',
            strokeThickness: 2
        });
        buttonText.setOrigin(0.5);
        
        this.spinButton.add([buttonBg, buttonText]);
        this.spinButton.setInteractive(new Phaser.Geom.Rectangle(-50, -25, 100, 50), Phaser.Geom.Rectangle.Contains);
        
        // Button interactions
        this.spinButton.on('pointerover', () => {
            if (!this.isSpinning) {
                this.spinButton.setScale(1.05);
                buttonBg.clear();
                buttonBg.fillStyle(0x00FFFF, 1);
                buttonBg.fillRoundedRect(-50, -25, 100, 50, 25);
                buttonBg.lineStyle(4, 0xFF00FF, 1);
                buttonBg.strokeRoundedRect(-50, -25, 100, 50, 25);
                buttonBg.lineStyle(2, 0xFFFFFF, 0.6);
                buttonBg.strokeRoundedRect(-47, -22, 94, 44, 22);
            }
        });
        
        this.spinButton.on('pointerout', () => {
            this.spinButton.setScale(1);
            buttonBg.clear();
            buttonBg.fillStyle(0xFF00FF, 1);
            buttonBg.fillRoundedRect(-50, -25, 100, 50, 25);
            buttonBg.lineStyle(4, 0x00FFFF, 1);
            buttonBg.strokeRoundedRect(-50, -25, 100, 50, 25);
            buttonBg.lineStyle(2, 0xFFFFFF, 0.6);
            buttonBg.strokeRoundedRect(-47, -22, 94, 44, 22);
        });
    }
    
    private createControlButton(x: number, y: number, text: string, style: any): Phaser.GameObjects.Container {
        const container = this.add.container(x, y);
        
        const bg = this.add.graphics();
        bg.fillStyle(0xFF00FF, 1);
        bg.fillRoundedRect(-15, -15, 30, 30, 5);
        bg.lineStyle(2, 0x00FFFF, 1);
        bg.strokeRoundedRect(-15, -15, 30, 30, 5);
        
        const buttonText = this.add.text(0, 0, text, style);
        buttonText.setOrigin(0.5);
        
        container.add([bg, buttonText]);
        container.setInteractive(new Phaser.Geom.Circle(0, 0, 20), Phaser.Geom.Circle.Contains);
        
        container.on('pointerover', () => container.setScale(1.1));
        container.on('pointerout', () => container.setScale(1));
        
        return container;
    }
    
    private setupBettingHandlers(): void {
        this.linesUpButton.on('pointerdown', () => {
            const state = this.gameEngine.getState();
            if (state.activeLines < 20) {
                this.gameEngine.setActiveLines(state.activeLines + 1);
                this.updateUI();
                this.updateActivePaylineDisplay();
            }
        });
        
        this.linesDownButton.on('pointerdown', () => {
            const state = this.gameEngine.getState();
            if (state.activeLines > 1) {
                this.gameEngine.setActiveLines(state.activeLines - 1);
                this.updateUI();
                this.updateActivePaylineDisplay();
            }
        });
        
        this.betUpButton.on('pointerdown', () => {
            const state = this.gameEngine.getState();
            if (state.betPerLine < 10) {
                this.gameEngine.setBetPerLine(state.betPerLine + 1);
                this.updateUI();
            }
        });
        
        this.betDownButton.on('pointerdown', () => {
            const state = this.gameEngine.getState();
            if (state.betPerLine > 1) {
                this.gameEngine.setBetPerLine(state.betPerLine - 1);
                this.updateUI();
            }
        });
    }
    
    private createPaylineIndicators(): void {
        // Create graphics objects for each payline
        for (let i = 0; i < 20; i++) {
            const lineGraphics = this.add.graphics();
            lineGraphics.setVisible(false);
            this.paylineGraphics.push(lineGraphics);
        }
    }
    
    private showPayline(lineIndex: number, color: number = 0xFFFF00, alpha: number = 0.8): void {
        if (lineIndex >= this.paylineGraphics.length) return;
        
        const graphics = this.paylineGraphics[lineIndex];
        graphics.clear();
        graphics.setVisible(true);
        
        const { width, height } = this.cameras.main;
        const startX = (width - (5 * this.REEL_WIDTH + 4 * this.REEL_SPACING)) / 2;
        const startY = (height - this.REEL_HEIGHT) / 2 - 50;
        
        // Get payline pattern from config
        const paylineConfig = this.gameEngine.getConfig();
        const line = paylineConfig.paylines[lineIndex];
        
        if (!line) return;
        
        graphics.lineStyle(4, color, alpha);
        graphics.fillStyle(color, alpha * 0.3);
        
        // Draw line connecting the payline positions
        let isFirst = true;
        for (let i = 0; i < line.length; i++) {
            const [row, reel] = line[i];
            
            const symbolSpacing = (this.REEL_HEIGHT - 20) / 3;
            const symbolStartY = -this.REEL_HEIGHT/2 + symbolSpacing/2 + 10;
            
            const x = startX + reel * (this.REEL_WIDTH + this.REEL_SPACING) + this.REEL_WIDTH/2;
            const y = startY + this.REEL_HEIGHT/2 + symbolStartY + (row * symbolSpacing);
            
            if (isFirst) {
                graphics.beginPath();
                graphics.moveTo(x, y);
                isFirst = false;
            } else {
                graphics.lineTo(x, y);
            }
            
            // Draw circle at each position
            graphics.fillCircle(x, y, 8);
        }
        
        graphics.strokePath();
    }
    
    private showWinningPayline(lineIndex: number, color: number, delay: number = 0): void {
        if (lineIndex >= this.paylineGraphics.length) return;
        
        const graphics = this.paylineGraphics[lineIndex];
        graphics.clear();
        graphics.setVisible(true);
        
        const { width, height } = this.cameras.main;
        const startX = (width - (5 * this.REEL_WIDTH + 4 * this.REEL_SPACING)) / 2;
        const startY = (height - this.REEL_HEIGHT) / 2 - 50;
        
        // Get payline pattern from config
        const paylineConfig = this.gameEngine.getConfig();
        const line = paylineConfig.paylines[lineIndex];
        
        if (!line) return;
        
        // Start with the line invisible for the animation
        graphics.setAlpha(0);
        
        // Animate the line appearing with a pop effect
        this.tweens.add({
            targets: graphics,
            alpha: 1,
            duration: 300,
            delay: delay,
            ease: 'Power2',
            onComplete: () => {
                // Create flashing effect
                this.tweens.add({
                    targets: graphics,
                    alpha: 0.3,
                    duration: 400,
                    yoyo: true,
                    repeat: 5,
                    ease: 'Sine.easeInOut'
                });
            }
        });
        
        // Draw thicker line for winning
        graphics.lineStyle(6, color, 1.0);
        
        // Draw line connecting the payline positions
        let isFirst = true;
        const positions: {x: number, y: number}[] = [];
        
        for (let i = 0; i < line.length; i++) {
            const [row, reel] = line[i];
            
            const symbolSpacing = (this.REEL_HEIGHT - 20) / 3;
            const symbolStartY = -this.REEL_HEIGHT/2 + symbolSpacing/2 + 10;
            
            const x = startX + reel * (this.REEL_WIDTH + this.REEL_SPACING) + this.REEL_WIDTH/2;
            const y = startY + this.REEL_HEIGHT/2 + symbolStartY + (row * symbolSpacing);
            
            positions.push({x, y});
            
            if (isFirst) {
                graphics.beginPath();
                graphics.moveTo(x, y);
                isFirst = false;
            } else {
                graphics.lineTo(x, y);
            }
        }
        
        graphics.strokePath();
        
        // Animate circles popping at each position
        positions.forEach((pos, index) => {
            const circle = this.add.graphics();
            circle.fillStyle(color, 0.8);
            circle.fillCircle(0, 0, 0);
            circle.setPosition(pos.x, pos.y);
            
            this.tweens.add({
                targets: circle,
                scaleX: 1,
                scaleY: 1,
                duration: 200,
                delay: delay + (index * 50),
                ease: 'Back.out',
                onUpdate: () => {
                    circle.clear();
                    circle.fillStyle(color, 0.8);
                    circle.fillCircle(0, 0, 12 * circle.scaleX);
                },
                onComplete: () => {
                    // Pulse animation
                    this.tweens.add({
                        targets: circle,
                        scaleX: 1.3,
                        scaleY: 1.3,
                        alpha: 0.5,
                        duration: 400,
                        yoyo: true,
                        repeat: 5,
                        ease: 'Sine.easeInOut',
                        onUpdate: () => {
                            circle.clear();
                            circle.fillStyle(color, circle.alpha);
                            circle.fillCircle(0, 0, 12 * circle.scaleX);
                        },
                        onComplete: () => {
                            circle.destroy();
                        }
                    });
                }
            });
        });
        
        // Add line number indicators with animation
        const textStyle = {
            fontFamily: 'Orbitron',
            fontSize: '20px',
            color: Phaser.Display.Color.IntegerToColor(color).rgba,
            stroke: '#000000',
            strokeThickness: 3,
            backgroundColor: '#000000',
            padding: { x: 4, y: 2 }
        };
        
        // Calculate position for line numbers
        const firstPos = line[0];
        const lastPos = line[line.length - 1];
        const avgRow = (firstPos[0] + lastPos[0]) / 2;
        const symbolSpacing = (this.REEL_HEIGHT - 20) / 3;
        const symbolStartY = -this.REEL_HEIGHT/2 + symbolSpacing/2 + 10;
        const textY = startY + this.REEL_HEIGHT/2 + symbolStartY + (avgRow * symbolSpacing);
        
        // Animated left number
        const leftNumber = this.add.text(startX - 35, textY, `${lineIndex + 1}`, textStyle);
        leftNumber.setOrigin(0.5);
        leftNumber.setScale(0);
        
        this.tweens.add({
            targets: leftNumber,
            scaleX: 1.2,
            scaleY: 1.2,
            duration: 300,
            delay: delay,
            ease: 'Back.out',
            onComplete: () => {
                this.tweens.add({
                    targets: leftNumber,
                    scaleX: 1,
                    scaleY: 1,
                    duration: 200
                });
            }
        });
        
        // Animated right number
        const rightX = startX + 5 * this.REEL_WIDTH + 4 * this.REEL_SPACING + 35;
        const rightNumber = this.add.text(rightX, textY, `${lineIndex + 1}`, textStyle);
        rightNumber.setOrigin(0.5);
        rightNumber.setScale(0);
        
        this.tweens.add({
            targets: rightNumber,
            scaleX: 1.2,
            scaleY: 1.2,
            duration: 300,
            delay: delay,
            ease: 'Back.out',
            onComplete: () => {
                this.tweens.add({
                    targets: rightNumber,
                    scaleX: 1,
                    scaleY: 1,
                    duration: 200
                });
            }
        });
        
        // Store texts for cleanup
        graphics.setData('lineNumberTexts', [leftNumber, rightNumber]);
    }
    
    private hideAllPaylines(): void {
        this.paylineGraphics.forEach(graphics => {
            // Clean up any line number texts
            const lineNumberTexts = graphics.getData('lineNumberTexts');
            if (lineNumberTexts && Array.isArray(lineNumberTexts)) {
                lineNumberTexts.forEach(text => text.destroy());
                graphics.setData('lineNumberTexts', null);
            }
            
            graphics.setVisible(false);
            graphics.clear();
        });
    }
    
    private updateActivePaylineDisplay(): void {
        // Hide all paylines first
        this.hideAllPaylines();
        
        // Instead of showing all lines at once, we'll cycle through them
        this.startPaylineCycle();
    }
    
    private startPaylineCycle(): void {
        // Stop any existing cycle
        if (this.paylineCycleTimer) {
            this.paylineCycleTimer.destroy();
        }
        
        const state = this.gameEngine.getState();
        let currentLine = 0;
        
        // Show the first line
        if (state.activeLines > 0) {
            this.showPaylineSubtle(0);
        }
        
        // Cycle through active paylines every 2 seconds
        this.paylineCycleTimer = this.time.addEvent({
            delay: 2000,
            callback: () => {
                this.hideAllPaylines();
                currentLine = (currentLine + 1) % state.activeLines;
                if (state.activeLines > 0) {
                    this.showPaylineSubtle(currentLine);
                }
            },
            loop: true
        });
    }
    
    private showPaylineSubtle(lineIndex: number): void {
        if (lineIndex >= this.paylineGraphics.length) return;
        
        const graphics = this.paylineGraphics[lineIndex];
        graphics.clear();
        graphics.setVisible(true);
        
        const { width, height } = this.cameras.main;
        const startX = (width - (5 * this.REEL_WIDTH + 4 * this.REEL_SPACING)) / 2;
        const startY = (height - this.REEL_HEIGHT) / 2 - 50;
        
        // Get payline pattern from config
        const paylineConfig = this.gameEngine.getConfig();
        const line = paylineConfig.paylines[lineIndex];
        
        if (!line) return;
        
        // Use theme payline colors
        const colors = this.currentTheme.colors.paylineColors;
        const color = colors[lineIndex % colors.length];
        
        // Draw thick solid line with high opacity
        graphics.lineStyle(4, color, 1.0);
        
        // Draw continuous line connecting the payline positions
        let isFirst = true;
        for (let i = 0; i < line.length; i++) {
            const [row, reel] = line[i];
            
            const symbolSpacing = (this.REEL_HEIGHT - 20) / 3;
            const symbolStartY = -this.REEL_HEIGHT/2 + symbolSpacing/2 + 10;
            
            const x = startX + reel * (this.REEL_WIDTH + this.REEL_SPACING) + this.REEL_WIDTH/2;
            const y = startY + this.REEL_HEIGHT/2 + symbolStartY + (row * symbolSpacing);
            
            if (isFirst) {
                graphics.beginPath();
                graphics.moveTo(x, y);
                isFirst = false;
            } else {
                graphics.lineTo(x, y);
            }
        }
        
        graphics.strokePath();
        
        // Add solid circles at each symbol position for better visibility
        graphics.fillStyle(color, 1.0);
        for (let i = 0; i < line.length; i++) {
            const [row, reel] = line[i];
            
            const symbolSpacing = (this.REEL_HEIGHT - 20) / 3;
            const symbolStartY = -this.REEL_HEIGHT/2 + symbolSpacing/2 + 10;
            
            const x = startX + reel * (this.REEL_WIDTH + this.REEL_SPACING) + this.REEL_WIDTH/2;
            const y = startY + this.REEL_HEIGHT/2 + symbolStartY + (row * symbolSpacing);
            
            graphics.fillCircle(x, y, 8);
        }
        
        // Add line number indicators on both left and right sides
        const textStyle = {
            fontFamily: 'Orbitron',
            fontSize: '16px',
            color: Phaser.Display.Color.IntegerToColor(color).rgba,
            stroke: '#000000',
            strokeThickness: 2,
            backgroundColor: '#000000',
            padding: { x: 4, y: 2 }
        };
        
        // Calculate the middle row position for the line number
        const firstPos = line[0];
        const lastPos = line[line.length - 1];
        const avgRow = (firstPos[0] + lastPos[0]) / 2;
        const symbolSpacing = (this.REEL_HEIGHT - 20) / 3;
        const symbolStartY = -this.REEL_HEIGHT/2 + symbolSpacing/2 + 10;
        const textY = startY + this.REEL_HEIGHT/2 + symbolStartY + (avgRow * symbolSpacing);
        
        // Left side number
        const leftNumber = this.add.text(startX - 35, textY, `${lineIndex + 1}`, textStyle);
        leftNumber.setOrigin(0.5);
        
        // Right side number
        const rightX = startX + 5 * this.REEL_WIDTH + 4 * this.REEL_SPACING + 35;
        const rightNumber = this.add.text(rightX, textY, `${lineIndex + 1}`, textStyle);
        rightNumber.setOrigin(0.5);
        
        // Store both texts with the graphics so we can clean them up
        graphics.setData('lineNumberTexts', [leftNumber, rightNumber]);
    }
    
    private showWinningLines(wins: any[]): void {
        // Stop cycling when showing winning lines
        if (this.paylineCycleTimer) {
            this.paylineCycleTimer.destroy();
            this.paylineCycleTimer = undefined;
        }
        
        this.hideAllPaylines();
        
        wins.forEach((win, index) => {
            const lineIndex = win.lineNumber - 1;
            const colors = this.currentTheme.colors.paylineColors;
            const color = colors[index % colors.length];
            
            // Use the new animated winning payline display
            this.showWinningPayline(lineIndex, color, index * 200);
        });
    }
    
    
    private setupInputHandlers(): void {
        this.spinButton.on('pointerdown', () => {
            if (!this.isSpinning) {
                this.spin();
            }
        });
        
        this.spinButton.on('pointerover', () => {
            this.spinButton.setScale(1.1);
        });
        
        this.spinButton.on('pointerout', () => {
            this.spinButton.setScale(1);
        });
        
        this.input.keyboard?.on('keydown-SPACE', () => {
            if (!this.isSpinning) {
                this.spin();
            }
        });
    }
    
    private async spin(): Promise<void> {
        if (this.isSpinning) return;
        
        this.isSpinning = true;
        this.winText.setVisible(false);
        
        // Stop payline cycling during spin
        if (this.paylineCycleTimer) {
            this.paylineCycleTimer.destroy();
            this.paylineCycleTimer = undefined;
        }
        this.hideAllPaylines();
        
        const state = this.gameEngine.getState();
        if (state.balance < state.totalBet && state.freeSpinsRemaining === 0) {
            this.showMessage('INSUFFICIENT BALANCE!');
            this.isSpinning = false;
            return;
        }
        
        if (state.freeSpinsRemaining === 0) {
            if (!this.gameEngine.deductBet()) {
                this.showMessage('INSUFFICIENT BALANCE!');
                this.isSpinning = false;
                return;
            }
        }
        
        this.updateUI();
        
        const result = this.gameEngine.spin();
        
        await this.animateReelSpin(result);
        
        await this.animateReelStop(result);
        
        if (result.totalWin > 0) {
            await this.showWin(result);
        }
        
        if (result.triggeredFeatures.includes('free_spins')) {
            this.showMessage('FREE SPINS TRIGGERED!');
        }
        
        this.updateUI();

        // Track spin statistics
        this.trackSpinStats(result);

        // Save balance to database
        await this.saveBalance(result);

        this.isSpinning = false;

        // Restart payline cycling after spin
        this.updateActivePaylineDisplay();
    }

    private async saveBalance(result: SpinResult): Promise<void> {
        const userManager = UserManager.getInstance();
        const state = this.gameEngine.getState();
        const won = result.totalWin;
        const lost = result.totalWin > 0 ? 0 : state.totalBet;

        console.log(`Slots: Saving balance ${state.balance}, won: ${won}, lost: ${lost}`);
        await userManager.updateBalance(state.balance, won, lost);
    }
    
    private async animateReelSpin(result: SpinResult): Promise<void> {
        const promises: Promise<void>[] = [];
        
        this.reelContainers.forEach((container, index) => {
            const delay = index * 200;
            
            const promise = new Promise<void>((resolve) => {
                this.time.delayedCall(delay, () => {
                    const originalY = container.y;
                    let spinCount = 0;
                    const maxSpins = 4;
                    
                    const performSpin = () => {
                        // Create extra symbols above for rolling effect
                        this.createRollingSymbols(container, index);
                        
                        this.tweens.add({
                            targets: container,
                            y: originalY + this.REEL_HEIGHT,
                            duration: 300,
                            ease: 'Linear',
                            onComplete: () => {
                                // Reset position
                                container.y = originalY;
                                
                                spinCount++;
                                if (spinCount < maxSpins) {
                                    // Show random symbols during spinning
                                    this.randomizeSymbols(index);
                                    performSpin();
                                } else {
                                    // On final spin, show the actual result
                                    this.setFinalSymbols(index, result.reels[index]);
                                    // Clean up extra symbols
                                    this.cleanupRollingSymbols(container);
                                    resolve();
                                }
                            }
                        });
                    };
                    
                    performSpin();
                });
            });
            
            promises.push(promise);
        });
        
        await Promise.all(promises);
    }
    
    private randomizeSymbols(reelIndex: number): void {
        const symbols = Object.values(SymbolType).filter(s => 
            s !== SymbolType.WILD && s !== SymbolType.SCATTER
        );
        
        this.symbolSprites[reelIndex].forEach(symbolContainer => {
            const randomSymbol = symbols[Math.floor(Math.random() * symbols.length)];
            this.updateSymbolContainer(symbolContainer, randomSymbol);
        });
    }
    
    private updateSymbolContainer(container: Phaser.GameObjects.Container, symbolType: SymbolType): void {
        
        // Clear existing graphics
        container.removeAll(true);
        container.setData('symbolType', symbolType);
        
        // Recreate with new symbol
        this.createSymbolGraphics(container, symbolType);
    }
    
    private setFinalSymbols(reelIndex: number, finalSymbols: SymbolType[]): void {
        finalSymbols.forEach((symbolType, rowIndex) => {
            if (this.symbolSprites[reelIndex] && this.symbolSprites[reelIndex][rowIndex]) {
                this.updateSymbolContainer(this.symbolSprites[reelIndex][rowIndex], symbolType);
            }
        });
    }
    
    private createRollingSymbols(reelContainer: Phaser.GameObjects.Container, reelIndex: number): void {
        const symbolSpacing = (this.REEL_HEIGHT - 20) / 3;
        const symbolStartY = -this.REEL_HEIGHT/2 + symbolSpacing/2 + 10;
        
        // Add 3 extra symbols above the visible area for rolling effect
        for (let i = 0; i < 3; i++) {
            const y = symbolStartY - symbolSpacing * (3 - i);
            const randomSymbol = this.getRandomSymbol();
            
            const symbolContainer = this.add.container(0, y);
            symbolContainer.setData('symbolType', randomSymbol);
            symbolContainer.setData('isRolling', true);
            
            this.createSymbolGraphics(symbolContainer, randomSymbol);
            reelContainer.add(symbolContainer);
        }
    }
    
    private cleanupRollingSymbols(reelContainer: Phaser.GameObjects.Container): void {
        const symbolsToRemove: Phaser.GameObjects.Container[] = [];
        
        reelContainer.list.forEach((child) => {
            if (child.getData && child.getData('isRolling')) {
                symbolsToRemove.push(child as Phaser.GameObjects.Container);
            }
        });
        
        symbolsToRemove.forEach(symbol => {
            symbol.destroy();
        });
    }
    
    private getRandomSymbol(): SymbolType {
        const symbols = Object.values(SymbolType).filter(s => 
            s !== SymbolType.WILD && s !== SymbolType.SCATTER
        );
        return symbols[Math.floor(Math.random() * symbols.length)];
    }
    
    private async animateReelStop(result: SpinResult): Promise<void> {
        const promises: Promise<void>[] = [];
        
        result.reels.forEach((reel, reelIndex) => {
            const delay = reelIndex * 150;
            
            const promise = new Promise<void>((resolve) => {
                this.time.delayedCall(delay, () => {
                    // Symbols are already set in animateReelSpin, just do the stop animation
                    const container = this.reelContainers[reelIndex];
                    this.tweens.add({
                        targets: container,
                        y: container.y - 20,
                        duration: 100,
                        ease: 'Back.out',
                        yoyo: true,
                        onComplete: () => resolve()
                    });
                    
                    this.cameras.main.shake(50, 0.005);
                });
            });
            
            promises.push(promise);
        });
        
        await Promise.all(promises);
    }
    
    private async showWin(result: SpinResult): Promise<void> {
        const winAmount = result.totalWin;
        const state = this.gameEngine.getState();
        
        let winMessage = '';
        if (winAmount >= state.totalBet * 50) {
            winMessage = `MEGA WIN! $${winAmount}`;
        } else if (winAmount >= state.totalBet * 20) {
            winMessage = `BIG WIN! $${winAmount}`;
        } else {
            winMessage = `WIN $${winAmount}`;
        }
        
        this.winText.setText(winMessage);
        
        // Adaptive font sizing - shrink text if it's too wide for the container
        const maxWidth = 150; // Available width in the win container
        this.winText.setScale(1, 1); // Reset to measure actual size
        const textBounds = this.winText.getBounds();
        
        let targetScaleX = 1;
        if (textBounds.width > maxWidth) {
            targetScaleX = maxWidth / textBounds.width;
        }
        
        this.winText.setVisible(true);
        this.winText.setScale(0); // Reset for animation
        
        this.tweens.add({
            targets: this.winText,
            scaleX: targetScaleX,
            scaleY: 1,
            duration: 500,
            ease: 'Back.out'
        });
        
        // Show winning paylines
        this.showWinningLines(result.wins);
        
        // Animate winning symbols
        result.wins.forEach(win => {
            win.positions.forEach(pos => {
                const sprite = this.symbolSprites[pos.reel][pos.row];
                this.tweens.add({
                    targets: sprite,
                    scaleX: 1.2,
                    scaleY: 1.2,
                    duration: 300,
                    yoyo: true,
                    repeat: 3
                });
            });
        });
        
        await new Promise(resolve => this.time.delayedCall(3000, resolve));
        
        // Hide paylines after win display
        this.hideAllPaylines();
    }
    
    private showMessage(text: string): void {
        const { width, height } = this.cameras.main;
        
        const message = this.add.text(width / 2, height / 2, text, {
            fontFamily: 'Bungee',
            fontSize: '48px',
            color: '#FFFF00',
            stroke: '#FF00FF',
            strokeThickness: 4
        });
        message.setOrigin(0.5);
        message.setScale(0);
        
        this.tweens.add({
            targets: message,
            scaleX: 1,
            scaleY: 1,
            duration: 500,
            ease: 'Back.out',
            onComplete: () => {
                this.time.delayedCall(2000, () => {
                    this.tweens.add({
                        targets: message,
                        alpha: 0,
                        duration: 500,
                        onComplete: () => message.destroy()
                    });
                });
            }
        });
    }
    
    private updateUI(): void {
        const state = this.gameEngine.getState();
        this.balanceText.setText(`$${state.balance}`);
        this.betText.setText(`$${state.totalBet}`);
        
        // Update betting controls
        this.linesText.setText(`${state.activeLines}`);
        this.betPerLineText.setText(`$${state.betPerLine}`);
        
        if (state.freeSpinsRemaining > 0) {
            this.freeSpinsText.setText(`FREE SPINS: ${state.freeSpinsRemaining}`);
            this.freeSpinsText.setVisible(true);
        } else {
            this.freeSpinsText.setVisible(false);
        }
    }
    
    private createPlayerRankings(x: number, y: number, width: number, height: number): void {
        // Background
        const bg = this.add.graphics();
        bg.fillStyle(0x000000, 0.8);
        bg.fillRoundedRect(x, y, width, height, 10);
        bg.lineStyle(2, 0xFFD700, 1);
        bg.strokeRoundedRect(x, y, width, height, 10);
        
        // Title
        const title = this.add.text(x + width / 2, y + 25,
            'PLAYER RANKINGS', {
            fontFamily: 'Orbitron',
            fontSize: '20px',
            color: '#FFD700',
            stroke: '#000000',
            strokeThickness: 2
        });
        title.setOrigin(0.5);
        
        // High score entries
        const startY = y + 60;
        for (let i = 0; i < 10; i++) {
            const scoreY = startY + (i * 30);
            const scoreText = this.add.text(x + 15, scoreY, '', {
                fontFamily: 'Courier',
                fontSize: '12px',
                color: '#FFFFFF'
            });
            this.rankingTexts.push(scoreText);
        }
        
        this.updateRankingsDisplay();
    }
    
    private async loadPlayerRankings(): Promise<void> {
        if (!this.auth) return;

        try {
            const response = await fetch(`${this.auth.getAdminServerUrl()}/api/rankings`);
            if (response.ok) {
                this.playerRankings = await response.json();
                this.updateRankingsDisplay();
            }
        } catch (error) {
            console.error('Failed to load player rankings:', error);
        }
    }

    private updateRankingsDisplay(): void {
        for (let i = 0; i < this.rankingTexts.length; i++) {
            if (i < this.playerRankings.length) {
                const player = this.playerRankings[i];
                const text = `${i + 1}. ${player.name.substring(0, 8)}: $${player.balance}`;
                this.rankingTexts[i].setText(text);
            } else {
                this.rankingTexts[i].setText('');
            }
        }
    }

    private updatePlayerStatsDisplay(): void {
        if (this.playerStatsTexts.length >= 3) {
            this.playerStatsTexts[0].setText(`Spins Played: ${this.playerStats.handsPlayed}`);
            this.playerStatsTexts[1].setText(`Best Win: $${this.playerStats.biggestWin}`);
            this.playerStatsTexts[2].setText(`Total Won: $${this.playerStats.totalWon}`);
        }
    }
    
    private trackSpinStats(result: SpinResult): void {
        if (!this.currentUser) return;

        // Increment spins played
        this.playerStats.handsPlayed++;

        // Track total money won/lost
        if (result.totalWin > 0) {
            this.playerStats.totalWon += result.totalWin;

            // Check if this is the biggest win
            if (result.totalWin > this.playerStats.biggestWin) {
                this.playerStats.biggestWin = result.totalWin;
            }

            // Track best "hand" for slots (we'll use the win description)
            if (result.totalWin > this.getWinValueFromDescription(this.playerStats.bestHand)) {
                this.playerStats.bestHand = `$${result.totalWin} Win`;
            }
        } else {
            this.playerStats.totalLost += this.gameEngine.getState().totalBet;
        }

        // Update the display
        this.updatePlayerStatsDisplay();

        // Update server stats
        this.updateServerStats();
    }

    private getWinValueFromDescription(description: string): number {
        if (!description) return 0;
        const match = description.match(/\$(\d+)/);
        return match ? parseInt(match[1]) : 0;
    }

    private async updateServerStats(): Promise<void> {
        if (!this.currentUser || !this.auth) return;

        const state = this.gameEngine.getState();

        try {
            const response = await fetch(`${this.auth.getAdminServerUrl()}/api/users/${this.currentUser.id}/stats`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': this.auth.getApiKey() || ''
                },
                body: JSON.stringify({
                    hands_played: this.playerStats.handsPlayed,
                    best_hand: this.playerStats.bestHand,
                    biggest_win: this.playerStats.biggestWin,
                    balance: state.balance,
                    won: this.playerStats.totalWon,
                    lost: this.playerStats.totalLost
                })
            });

            if (response.ok) {
                console.log('Stats updated successfully');
            }
        } catch (error) {
            console.error('Failed to update stats:', error);
        }
    }
    
    
    private setupNameInput(): void {
        // Name input is now created in createLeftSideControls
        // This method is kept for compatibility but doesn't need to do anything
    }
    
    private promptForName(): void {
        const name = prompt('Enter your name (max 8 characters):', this.playerName);
        if (name && name.trim()) {
            this.playerName = name.trim().substring(0, 8);
            localStorage.setItem('playerName', this.playerName);
            this.nameInputText.setText(`Player: ${this.playerName}`);
        }
    }
    
    private createThemeSelector(): void {
        if (!this.themeSelector) {
            this.themeSelector = new ThemeSelector();
        }
    }

    private createSoundControls(): void {
        if (!this.soundControls) {
            this.soundControls = new SoundControlsPanel(this);
        }
    }
    
    private refreshPlayerBalance(): void {
        // Get current profile balance
        const userManager = UserManager.getInstance();
        const user = userManager.getCurrentUser();

        console.log('[GameScene] refreshPlayerBalance - user:', user);

        if (!user) {
            console.warn('[GameScene] No user found, cannot refresh balance');
            return;
        }

        // Update engine balance to match profile balance
        const profileBalance = user.balance || 1000;
        const currentEngineBalance = this.gameEngine.getState().balance;

        console.log(`[GameScene] Refreshing balance - Profile: ${profileBalance}, Current Engine: ${currentEngineBalance}`);

        this.gameEngine.setBalance(profileBalance);

        // Update UI display
        this.updateUI();

        console.log(`[GameScene] Slots balance refreshed to: ${profileBalance}`);
    }

    destroy(): void {
        if (this.themeSelector) {
            this.themeSelector.destroy();
            this.themeSelector = undefined;
        }
        if (this.soundControls) {
            this.soundControls.destroy();
            this.soundControls = undefined;
        }
    }

    shutdown(): void {
        if (this.themeSelector) {
            this.themeSelector.destroy();
            this.themeSelector = undefined;
        }
        if (this.soundControls) {
            this.soundControls.destroy();
            this.soundControls = undefined;
        }
    }
}